import { z } from "zod";
import { upsertStaffLiveLocationSchema } from "./staff-location.ts";

/**
 * Booking schemas — the shared contract ("validate twice, define once").
 * The RHF booking form and the bookings Edge Function parse the SAME schema.
 * Identity (client_id) and hub scoping come from the verified JWT server-side,
 * never from this payload (security.md).
 */
export const createBookingSchema = z
  .object({
    serviceVariantId: z.string().uuid(),
    hubId: z.string().uuid(),
    scheduledAt: z.string().datetime({ offset: true }),
    // How the REMAINDER is paid. With GiftPasses attached the stored method
    // becomes giftpass / giftpass_partial server-side (Flows §1/§3).
    paymentMethod: z.enum(["online", "cash"]),
    // Passes the client already scanned via POST /giftpass/redeem — the
    // server verifies each is redeemed BY this client and still unlinked.
    giftpassPassIds: z.array(z.string().uuid()).max(10).default([]),
    addOnIds: z.array(z.string().uuid()).max(10).default([]),
    // Therapist selection (Business Logic §1). A SPECIFIC favorite bypasses
    // the queue — the server verifies it against the favorites table, never
    // trusts this field alone (SECURITY §9a).
    therapistMode: z.enum(["random", "favorites_only", "favorites_first"]).default("random"),
    specificTherapistId: z.string().uuid().optional(),
    // Either a saved address or an inline one (map pin optional).
    addressId: z.string().uuid().optional(),
    addressText: z.string().min(5).max(500).optional(),
    geo: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .optional(),
  })
  .refine((v) => v.addressId || v.addressText, {
    message: "Provide addressId or addressText.",
    path: ["addressId"],
  });

export type CreateBookingInput = z.infer<typeof createBookingSchema>;

/**
 * POST /bookings/assisted — staff takes a phone-in/walk-in booking on a
 * client's behalf (Hub Manager own hub · admin · superadmin; audit-logged).
 *
 * Deliberately narrower than createBookingSchema:
 * - `clientPhone` resolves an EXISTING registered client server-side; the
 *   response echoes no profile data back (data-wall.md). No guest accounts —
 *   walk-ins without an account register in the app first (Open Item if that
 *   needs to change).
 * - Cash only: an online remainder needs the client's own PayMongo checkout,
 *   and GiftPass scanning lives in the client app.
 * - No therapist selection: favorites are a client-only concept, so assisted
 *   bookings always go through the earnings queue (SECURITY §9a).
 * - `addressText` only: saved addresses are walled PII the staff role cannot
 *   list.
 */
export const createAssistedBookingSchema = z.object({
  clientPhone: z.string().regex(/^\+639\d{9}$/, "PH mobile in +639XXXXXXXXX format"),
  hubId: z.string().uuid(),
  serviceVariantId: z.string().uuid(),
  scheduledAt: z.string().datetime({ offset: true }),
  addOnIds: z.array(z.string().uuid()).max(10).default([]),
  addressText: z.string().min(5).max(500),
  geo: z
    .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
    .optional(),
});

export type CreateAssistedBookingInput = z.infer<typeof createAssistedBookingSchema>;

/** PATCH /bookings/:id/status — the transition matrix enforces validity. */
export const updateBookingStatusSchema = z.object({
  to: z.enum([
    "awaiting_rider",
    "dispatched",
    "en_route_to_client",
    "arrived",
    "in_session",
    "completed",
    "cancelled",
  ]),
  /**
   * When a Hub Manager fixes a forgotten tap ("fix session start times" —
   * UX Notes §Hub Manager), the real moment the session started. Recorded in
   * booking_events.meta; only honoured for override roles server-side.
   */
  correctedAt: z.string().datetime({ offset: true }).optional(),
  /**
   * Why an override role killed this booking. Required by the admin UI before
   * it enables the button, and recorded in BOTH `booking_events.meta` and an
   * `audit_log` row.
   *
   * Optional here rather than conditionally required, because the client and
   * the therapist also cancel through this endpoint and neither is asked for
   * one — the vault defines no cancellation-reason taxonomy for them
   * (never-invent-policy.md). Server-side the reason is only read when
   * `to === "cancelled"`.
   *
   * `cancelled` is TERMINAL — the transition matrix has no edge out of it — and
   * `booking_events` is a state-machine log, not an accountability one. Without
   * this, "who killed BNB-1234 and why" is answerable only as a bare actor_id.
   */
  reason: z.string().trim().min(1).max(500).optional(),
  /**
   * CORE-08 §4.7 / CXL-021 — this cancellation is system-caused or an approved
   * emergency, so no fee is charged EVEN AFTER PICKUP, no cancellation share is
   * paid, and the whole monetary payment returns to the client as Credits.
   *
   * ⚠ HONOURED ONLY FOR OVERRIDE ROLES, and the server decides that from the
   * verified JWT — a client sending `emergency: true` would otherwise refund
   * their own ₱100 fee. Optional here rather than role-gated in the schema
   * because one endpoint serves the client, the therapist and hub staff.
   *
   * Read only when `to === "cancelled"`, like `reason`.
   */
  emergency: z.boolean().optional(),
  /**
   * Support is entering the CLIENT's request to stop a Service that is running
   * (0276, Leon 2026-10-02): no refund, the Therapist is paid normally, and a
   * cash booking still owes its full amount. The opposite of `emergency`, which
   * is the company stopping it and refunds everything.
   *
   * ⚠ HONOURED ONLY FOR OVERRIDE ROLES and only on a booking that is In Service.
   * A client needs no flag: their own cancel mid-service IS the client's request
   * (online only — a cash client is refused and sent to Support).
   */
  clientRequested: z.boolean().optional(),
});

export type UpdateBookingStatusInput = z.infer<typeof updateBookingStatusSchema>;

/** POST /dispatch/trips — rider accepts the job; snapshot locks at creation. */
export const createTripSchema = z.object({
  bookingId: z.string().uuid(),
});

export type CreateTripInput = z.infer<typeof createTripSchema>;

/** @deprecated superseded by createBookingSchema — kept until the app scaffold migrates. */
export const bookingIntentSchema = z.object({
  clientId: z.string().uuid(),
  serviceId: z.string().uuid(),
  hubId: z.string().uuid(),
  scheduledAt: z.string().datetime(),
  idempotencyKey: z.string().uuid(),
});

export type BookingIntent = z.infer<typeof bookingIntentSchema>;

// ── Service Timer and Client No-Show — TEND-040…044, TEND-066 (0106) ─────────
//
// ⚠ NO TIMES TRAVEL IN ANY OF THESE. Every deadline is computed and stored by
// the server (TEND-063: "Device clocks cannot finish or extend server timers").
// A client that could send a start or end instant could extend its own service.

/** Confirm Drop-Off — starts the non-pausable 15-minute countdown. */
// 0362 — like Confirm Pick-Up, the slide carries her fresh fix: drop-off at
// the Client is now refused outside the radius. Optional so an older build
// still parses; the server then measures her last pin.
export const confirmDropOffSchema = z
  .object({ bookingId: z.string().uuid(), fix: z.lazy(() => pickupFixSchema).optional() })
  .strict();

/**
 * Start the Service manually during the countdown.
 *
 * There is deliberately no matching "end" schema: TEND-041 gives the therapist
 * no manual End Service action at all, and the client's confirmed modal is the
 * only approved early completion.
 */
export const startServiceSchema = z.object({ bookingId: z.string().uuid() }).strict();

/**
 * The blocking show-up answer — TEND-043/044.
 *
 * `showedUp: false` completes the booking as Client No-Show, so it is only ever
 * sent from a deliberate tap: "Never select No automatically; the Therapist must
 * answer."
 */
export const answerShowUpSchema = z
  .object({ bookingId: z.string().uuid(), showedUp: z.boolean() })
  .strict();

/** The CLIENT's confirmed early end — TEND-066. Client app only. */
export const clientEndServiceSchema = z.object({ bookingId: z.string().uuid() }).strict();

export type ConfirmDropOffInput = z.infer<typeof confirmDropOffSchema>;
export type StartServiceInput = z.infer<typeof startServiceSchema>;
export type AnswerShowUpInput = z.infer<typeof answerShowUpSchema>;
export type ClientEndServiceInput = z.infer<typeof clientEndServiceSchema>;

/**
 * A fresh fix taken AT the slide (0293). Optional: the route writes it to her
 * live pin through the same guards as `/staff-location/live`, then the server
 * measures. It is HER position only, exactly what her phone already reports
 * every 30 s — sent with the action so the 15 s freshness rule can be met.
 * Never a rider position and never a distance: the server still measures.
 */
export const pickupFixSchema = upsertStaffLiveLocationSchema.strict();
export type PickupFix = z.infer<typeof pickupFixSchema>;

/**
 * Confirm Pick-Up — TEND-034, after entering the motorcycle.
 *
 * TEND-035 makes GPS an UNLOCK, never a confirmation: "GPS does not complete
 * the stage." Since 0248 the server refuses outside 50 m of her rider; since
 * 0293 she may send her own fresh fix (`fix`) so that check reads a current pin.
 */
export const confirmPickUpSchema = z
  .object({ bookingId: z.string().uuid(), fix: pickupFixSchema.optional() })
  .strict();
export type ConfirmPickUpInput = z.infer<typeof confirmPickUpSchema>;

/**
 * Confirm Cash Received — TEND-056, the blocking screen after the Service.
 *
 * ⚠ NO AMOUNT TRAVELS, AND THAT IS THE RULE ITSELF. "Show a blocking Confirm
 * Cash Received screen with the exact final Total Payment; Therapist cannot
 * edit it." A field here would be a number the client app chose, and `.strict()`
 * means sending one is a validation failure rather than a silently ignored
 * extra. The server reads `booking_cash_payable()` from the row.
 */
export const confirmCashReceivedSchema = z.object({ bookingId: z.string().uuid() }).strict();

/**
 * The screen's other answer — a discrepancy, filed as Report an Issue.
 *
 * The note is optional because TEND-062 forbids requiring evidence before a
 * report is accepted; it is capped because it reaches a Support queue, not a
 * document store.
 */
export const reportCashDiscrepancySchema = z
  .object({ bookingId: z.string().uuid(), note: z.string().trim().min(1).max(500).optional() })
  .strict();

export type ConfirmCashReceivedInput = z.infer<typeof confirmCashReceivedSchema>;
export type ReportCashDiscrepancyInput = z.infer<typeof reportCashDiscrepancySchema>;

/**
 * The post-service Therapist Pickup Dispatch — TEND-037 (0111).
 *
 * ⚠ These address a DISPATCH, not a booking, and only a `hub` return has its
 * own confirmations. When her pickup is taking her to a next client, the ride is
 * that booking's own dispatch and `confirmPickUpSchema` above is the action —
 * one physical act, one control.
 */
export const confirmTherapistPickUpSchema = z
  .object({ dispatchId: z.string().uuid(), fix: pickupFixSchema.optional() })
  .strict();
export const confirmTherapistDropOffSchema = z.object({ dispatchId: z.string().uuid() }).strict();

export type ConfirmTherapistPickUpInput = z.infer<typeof confirmTherapistPickUpSchema>;

/**
 * 0295 — where her Return takes her after THIS job (Leon 2026-09-26): a
 * Drop-off she names, or `null` to clear it (back to Hub / Origin). Never the
 * starting pickup (TEND-011, To Follow), never a standing location.
 */
export const setReturnDropoffSchema = z
  .object({
    bookingId: z.string().uuid(),
    dropoff: z
      .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
        label: z.string().trim().min(1).max(80),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type SetReturnDropoffInput = z.infer<typeof setReturnDropoffSchema>;
export type ConfirmTherapistDropOffInput = z.infer<typeof confirmTherapistDropOffSchema>;
